import type { EventEvidenceReviewCaptureV4 } from './eventEvidenceReview';
import type { EventEvidenceReviewManifestV4, EventEvidenceReviewRetentionReceipt } from './researchManifest';
import type { GeoPolygon, GeoMultiPolygon } from './eventGeography';

export const FACILITY_EXPOSURE_MAX_CAPTURE_BYTES = 2 * 1024 * 1024;
export const FACILITY_EXPOSURE_MAX_DECODED_BYTES = 2 * 1024 * 1024;
export const FACILITY_EXPOSURE_MAX_CANDIDATES = 500;
export const FACILITY_EXPOSURE_MAX_DISPLAYED = 50;
export type FacilityCatalogRowV1 = [plantCode: string, name: string, state: string, county: string | null, latitude: number | null, longitude: number | null];
export type FacilityCatalogReceiptV1 = {
  provider: 'eia-860'; sourceVersion: 'eia860-2025'; reportingYear: 2025;
  publicationDate: '2026-09-10'; retrievedAt: null; retrievalTimeBasis: 'unrecorded';
  sourceFileMtime: '2026-10-13T16:03:33.000Z'; catalogCapturedAt: string;
  sourceUrl: 'https://www.eia.gov/electricity/data/eia860/xls/eia8602025.zip';
  sourceZipSha256: string; workbookPath: '2___Plant_Y2025.xlsx'; workbookSha256: string;
  worksheet: 'Plant'; firstWorksheetRow: 3; recordCount: 17349; missingCoordinateRows: 28;
  fields: ['plantCode','name','state','county','latitude','longitude'];
  coordinateMethod: 'reported-decimal-central-point-within-plant-property';
  coordinateDatum: 'unestablished'; coordinateAccuracy: 'unestablished';
  rights: 'public-domain'; buildVersion: 'eia8602025-all-rows-v1';
};
/** Full national source tuples. Replay must use these bytes, never a current asset. */
export type FacilityCatalogV1 = {
  version: 'tsm-facility-catalog-v1'; encoding: 'gzip-base64'; data: string;
  compressedSha256: string; decodedSha256: string; compressedBytes: number;
  decodedBytes: number; decoderMaxBytes: 2097152; receipt: FacilityCatalogReceiptV1;
};
export type FacilityExposureParent = {
  manifest: EventEvidenceReviewManifestV4; markdown: string; retentionReceipt: EventEvidenceReviewRetentionReceipt;
};
export type FacilityExposureSiteV1 = {
  sourceRowId: string; sourceWorksheetRow: number; plantCode: string; name: string;
  state: string; county: string | null; latitude: number; longitude: number;
  relation: 'inside-screen' | 'boundary-uncertain';
};
export type FacilityExposureResultV1 = {
  version: 'tsm-facility-exposure-result-v1'; status: 'partial' | 'insufficient_evidence';
  screeningState: 'complete' | 'candidate-limit-exceeded'; displayState: 'complete' | 'truncated';
  selectedBounds: [west: number, south: number, east: number, north: number];
  totalCatalogRows: number; coordinateValidRows: number; missingCoordinateRows: number;
  bboxCandidateCount: number; testedCount: number; insideCandidateCount: number | null;
  boundaryUncertainCount: number | null; outsideCount: number | null; displayedCount: number;
  sites: FacilityExposureSiteV1[]; missing: string[]; stopReason: string; parentBoundary: string;
  geographicExposure: 'unestablished'; operations: 'unestablished';
};
export type FacilityExposureEvidenceV1 = {
  version: 'tsm-facility-exposure-v1'; policy: 'eia860-reported-coordinate-screen-v1';
  mode: 'frozen-catalog-only'; method: 'planar-longitude-latitude-no-transform-no-buffer';
  reviewedAt: string; configDigest: string;
  binding: { parentReportId: string; parentManifestDigest: string; definitionDigest: string;
    selectedRevisionKey: string; sourceSnapshotDigest: string; selectedSourceRowDigest: string;
    geometryDigest: string; sourceReportId: string; sourceUrl: string;
    sourcePublishedAt: string | null; sourceUpdatedAt: string | null;
  };
  geometry: GeoPolygon | GeoMultiPolygon; catalog: FacilityCatalogV1;
};
export type EventEvidenceReviewCaptureV9 = Omit<EventEvidenceReviewCaptureV4, 'version'> & {
  version: 'tsm-event-evidence-review-capture-v9'; eventReview: FacilityExposureParent;
  facilityExposure: FacilityExposureEvidenceV1; facilityExposureResult: FacilityExposureResultV1;
};
export type FacilityExposurePlan = { state: 'ready' | 'unsupported'; reasons: string[]; selectedRevisionKey: string | null };
export type FacilityExposureCaptureInput = { parent: FacilityExposureParent; catalog: FacilityCatalogV1; asOf: string };
